import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react'
import {
  AUTH_REFRESH_TOKEN_STORAGE_KEY,
  AUTH_TOKEN_STORAGE_KEY,
  persistTokens,
  setSessionInvalidHandler,
} from '../services/api'
import * as authService from '../services/authService'
import { assertPlatformAdminRole, isPlatformAdminRole, normalizeAdminScope } from '../lib/platformAdminAccess'
import {
  FORBIDDEN_ROLE_SESSION_MESSAGE,
  SESSION_EXPIRED_MESSAGE,
  writeSessionMessage,
} from '../lib/sessionMessages'
import { syncAdminSentryUser } from '../lib/sentry'
import {
  AuthActionType,
  authReducer,
  createInitialAuthState,
} from './reducers/authReducer'

const STORAGE_USER = 'shopora_admin_user'

const AuthContext = createContext<any>(null)

function readStoredUser() {
  try {
    const raw = localStorage.getItem(STORAGE_USER)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(
    authReducer,
    undefined,
    () => createInitialAuthState(readStoredUser),
  )

  const clearLocalSession = useCallback((message?: string) => {
    if (message) writeSessionMessage(message)
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
    localStorage.removeItem(AUTH_REFRESH_TOKEN_STORAGE_KEY)
    localStorage.removeItem(STORAGE_USER)
    syncAdminSentryUser(null)
    dispatch({ type: AuthActionType.LOGOUT_COMPLETE })
  }, [])

  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)
    const user = readStoredUser()
    if (user && !token) {
      clearLocalSession()
      return
    }
    if (user && token && !isPlatformAdminRole(user.role)) {
      void authService.logout().finally(() => {
        clearLocalSession(FORBIDDEN_ROLE_SESSION_MESSAGE)
      })
    }
  }, [clearLocalSession])

  useEffect(() => {
    if (state.user) syncAdminSentryUser(state.user)
  }, [state.user])

  const login = useCallback(async (credentials) => {
    dispatch({ type: AuthActionType.LOGIN_START })
    try {
      const { user: u, token, refreshToken } = await authService.login(credentials)
      assertPlatformAdminRole(u.role)
      const storedUser = {
        ...u,
        adminScope: normalizeAdminScope(
          u.adminScope ?? (u.role === 'super_admin' ? 'ALL' : undefined),
        ),
      }
      if (refreshToken) {
        persistTokens(token, refreshToken)
      } else {
        localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token)
        localStorage.removeItem(AUTH_REFRESH_TOKEN_STORAGE_KEY)
      }
      localStorage.setItem(STORAGE_USER, JSON.stringify(storedUser))
      dispatch({ type: AuthActionType.LOGIN_SUCCESS, payload: storedUser })
      syncAdminSentryUser(storedUser)
      return storedUser
    } catch (e) {
      dispatch({ type: AuthActionType.LOGIN_FAILURE, payload: e?.message || 'Login failed' })
      throw e
    }
  }, [])

  const logout = useCallback(async () => {
    dispatch({ type: AuthActionType.LOGOUT_START })
    const refresh = localStorage.getItem(AUTH_REFRESH_TOKEN_STORAGE_KEY)
    clearLocalSession()
    try {
      await authService.logout(refresh)
    } catch {
      /* best-effort revoke */
    }
  }, [clearLocalSession])

  const logoutSessionExpired = useCallback(async () => {
    const refresh = localStorage.getItem(AUTH_REFRESH_TOKEN_STORAGE_KEY)
    clearLocalSession(SESSION_EXPIRED_MESSAGE)
    try {
      await authService.logout(refresh)
    } catch {
      /* best-effort revoke */
    }
  }, [clearLocalSession])

  useEffect(() => {
    setSessionInvalidHandler(() => {
      void logoutSessionExpired()
    })
    return () => setSessionInvalidHandler(null)
  }, [logoutSessionExpired])

  const clearError = useCallback(() => {
    dispatch({ type: AuthActionType.CLEAR_ERROR })
  }, [])

  const value = useMemo(
    () => ({
      user: state.user,
      isAuthenticated: Boolean(state.user),
      login,
      logout,
      loading: state.loading,
      error: state.error,
      clearError,
    }),
    [state.user, state.loading, state.error, login, logout, clearError],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
